import React, { useState } from 'react';
import { Box, Typography, Divider, Button, IconButton } from '@mui/material';
import { Delete as DeleteIcon, Settings as SettingsIcon } from '@mui/icons-material';
import LinkManager from './LinkManager';
import PlaySessionButton from '../PlaySessionButton';
import { deletePlayLog } from '../../services/api';
import Dialog from '../dialogs/Dialog';
import Tips from '../dialogs/Tips';

interface PlayLogToolsProps {
  gameId: number;
  isOwner: boolean;
  onSessionEnd?: () => void;
  onPlayLogDeleted?: () => void;
}

const PlayLogTools: React.FC<PlayLogToolsProps> = ({ gameId, isOwner, onSessionEnd, onPlayLogDeleted }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showManagementMenu, setShowManagementMenu] = useState(false);

  const handleDeletePlayLog = async () => {
    try {
      await deletePlayLog(gameId);
      setDeleteDialogOpen(false);
      if (onPlayLogDeleted) {
        onPlayLogDeleted();
      }
    } catch (error) {
      console.error('プレイログの削除に失敗しました:', error);
      alert('プレイログの削除に失敗しました');
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }}>
      {/* プレイセッション管理 */}
      {isOwner && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Typography variant="h6" sx={{ 
              fontSize: '0.75rem', 
              color: 'text.secondary',
              fontWeight: 500 
            }}>
              セッション計測
            </Typography>
            <Tips
              title="セッション計測"
              iconColor="rgba(255, 255, 255, 0.5)"
              content={
                <Box>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    リアルタイムで時間を計測してログを作成します。
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <li>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        既存のデイリーログがある場合は自動で時間を加算
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        計測ごとにセッションが+1され、正確なプレイ回数を記録
                      </Typography>
                    </li>
                  </Box>
                </Box>
              }
            />
          </Box>
          <PlaySessionButton 
            gameId={gameId}
            disabled={false}
            onSessionEnd={onSessionEnd}
          />
        </Box>
      )}

      {/* リンクマネージャー */}
      {isOwner && (
        <>
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
          <Box>
            <LinkManager gameId={gameId} showHeader={true} />
          </Box>
        </>
      )}

      {/* 管理メニュー */}
      {isOwner && (
        <>
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
          <Box>
            <Button
              onClick={() => setShowManagementMenu(!showManagementMenu)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                fontSize: '0.7rem',
                py: 0.75,
                mb: showManagementMenu ? 1 : 0,
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              {showManagementMenu ? '管理メニューを閉じる' : '管理メニューを開く'}
            </Button>
            
            {showManagementMenu && (
              <Button
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  fontSize: '0.7rem',
                  py: 0.75,
                  borderColor: 'rgba(255, 100, 100, 0.3)',
                  color: 'rgba(255, 100, 100, 0.8)',
                  '&:hover': {
                    borderColor: 'rgba(255, 100, 100, 0.5)',
                    backgroundColor: 'rgba(255, 100, 100, 0.05)'
                  }
                }}
              >
                プレイログを削除
              </Button>
            )}
          </Box>
        </>
      )}

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="プレイログを削除"
        showCloseButton={false}
        content={
          <Box>
            <Typography sx={{ mb: 2, fontSize: '0.9rem' }}>
              このゲームのプレイログを完全に削除しますか？
              <br />
              <strong>この操作は取り消せません。</strong>
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={() => setDeleteDialogOpen(false)}>
                キャンセル
              </Button>
              <Button 
                onClick={handleDeletePlayLog}
                variant="contained"
                color="error"
              >
                削除する
              </Button>
            </Box>
          </Box>
        }
      />
    </Box>
  );
};

export default PlayLogTools;